import React, { useState, useCallback, useMemo } from 'react';
import { UpCaret, DownCaret } from '../../../common/icons';
import Dropdown from '../../Dropdown';
import { 
    getLabels, 
    isHigherBetter, 
    getStatKey, 
    extractStats, 
    getTeamNames,
    getTabs
} from './statAdapter';

const StatsTable = ({sport, game, dataType, setDataType, dataTypeOptions, showCarets = true}) => {
    
    // Get available tabs for the current sport
    const availableTabs = useMemo(() => {
        return getTabs(sport);
    }, [sport]);
    
    // Set initial active tab to first available tab
    const [activeTab, setActiveTab] = useState(availableTabs[0]?.value || '');
    
    // Update activeTab when sport changes
    React.useEffect(() => {
        const tabs = getTabs(sport);
        if (tabs.length > 0 && !tabs.find(tab => tab.value === activeTab)) {
            setActiveTab(tabs[0].value);
        }
    }, [sport, activeTab]);
    
    // Define color constants
    const BETTER_COLOR = "#43a047"; // Green
    const WORSE_COLOR = "#e53935"; // Red
    
    // Get labels for current sport and category
    const labels = useMemo(() => {
        return getLabels(sport, activeTab);
    }, [sport, activeTab]);
    
    // Get team names for display
    const teamNames = useMemo(() => {
        return getTeamNames(sport, game);
    }, [sport, game]);
    
    // Extract stats for current sport and category
    const stats = useMemo(() => {
        return extractStats(sport, activeTab, game, dataType);
    }, [sport, activeTab, game, dataType]);
    
    // Memoize the compareStats function
    const compareStats = useCallback((stat, homeVal, awayVal) => {
        if (homeVal === awayVal) return null;
        
        const statKey = getStatKey(sport, activeTab, stat);
        const higherBetter = dataType === 'raw' ? isHigherBetter(sport, statKey)
            : dataType === 'rank' ? false
            : true;
        
        if (higherBetter) {
            return homeVal > awayVal ? 'home' : 'away';
        } else {
            return homeVal < awayVal ? 'home' : 'away';
        }
    }, [sport, activeTab, dataType]);
    
    // Memoize rendering table rows
    const renderTableRows = useMemo(() => {
        if (!stats || Object.keys(stats).length === 0) {
            return (
                <tr>
                    <td colSpan="3" className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                        Stats Unavailable
                    </td>
                </tr>
            );
        }
        
        const statsKeys = Object.keys(stats);
        
        return statsKeys.map((stat, statIdx) => {
            const homeValue = stats[stat][0];
            const awayValue = stats[stat][1];
            const better = showCarets ? compareStats(stat, homeValue, awayValue) : null;
            const isLast = statIdx === statsKeys.length - 1;
            const label = labels[stat] || stat;

            return (
                <tr key={stat}>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} px-3 py-3.5 text-xs xl:text-sm font-medium text-gray-900 dark:text-gray-100`}>
                        {label}
                    </td>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} px-3 py-3.5 text-xs xl:text-sm text-gray-900 dark:text-gray-100 ${better === 'home' ? 'font-medium text-green-700 dark:text-green-500' : better === 'away' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                        {homeValue}
                        {better === 'home' && (
                            <UpCaret className="inline-block  w-4 h-4" fillColor={BETTER_COLOR} />
                        )}
                        {better === 'away' && (
                            <DownCaret className="inline-block  w-4 h-4" fillColor={WORSE_COLOR} />
                        )}
                    </td>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} px-3 py-3.5 text-xs xl:text-sm text-gray-900 dark:text-gray-100 ${better === 'away' ? 'font-medium text-green-700 dark:text-green-500' : better === 'home' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                        {awayValue}
                        {better === 'away' && (
                            <UpCaret className="inline-block w-4 h-4" fillColor={BETTER_COLOR} />
                        )}
                        {better === 'home' && (
                            <DownCaret className="inline-block w-4 h-4" fillColor={WORSE_COLOR} />
                        )}
                    </td>
                </tr>
            );
        });
    }, [stats, labels, compareStats, showCarets, sport, activeTab, dataType, BETTER_COLOR, WORSE_COLOR]);
    
    // Don't render if no stats available
    if (!stats) {
        return null;
    }
    
    return (
        <div className="h-full flex flex-col px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6">
            <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-between gap-3 2xl:gap-0 mb-4 relative z-20 mt-1">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Team Stats
                </h1>
                <div className="flex flex-col xl:flex-row gap-3">
                    <Dropdown
                        id="data-type-select"
                        title="Select Data Type"
                        items={dataTypeOptions}
                        activeItem={dataType}
                        onSelect={setDataType}
                        size="md"
                        variant="default"
                        className="w-full md:w-auto"
                    />
                    <Dropdown
                        id="stat-type-select"
                        title="Select Stats Type"
                        items={availableTabs}
                        activeItem={activeTab}
                        onSelect={setActiveTab}
                        size="md"
                        variant="default"
                        className="w-full md:w-auto"
                    />
                </div>
            </div>
            
            <div className="flex-1 flex flex-col min-h-0 py-2">
                <div className="flex-1 overflow-y-auto shadow ring-1 ring-gray-300 ring-opacity-5 rounded-lg dark:ring-gray-700">
                    <table className="min-w-full w-full table-fixed border-separate border-spacing-0">
                        <thead>
                            <tr>
                                <th scope="col" className="w-auto sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs xl:text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">
                                    Stat
                                </th>
                                <th scope="col" className="w-[30%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs xl:text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">
                                    {teamNames.home}
                                </th>
                                <th scope="col" className="w-[30%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs xl:text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">
                                    {teamNames.away}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderTableRows}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StatsTable;